import {
  calculatePolicyInflationProtection,
  getInflationProtectionYears,
  getInflationRateFromQuote,
} from '@/utils/inflationParser';
import {
  CareEnvironment,
  FundingPolicyType,
  calcClientDerivatives,
  defaultsRetroactiveWaitingPeriodReimbursement,
  fundingPolicyTypeDefs,
  isCareMattersFamily,
} from '@shared';
import { cloneDeep } from 'lodash';
import { Quote } from '../model/quotesModel';
import { getPolicyTypeFromProductName } from './findPartnerPolicyFundingSourceId';

/**
 * Helper function to find discount policy type from any care environment that has a discount
 */
function findDiscountPolicyType(
  clientLocalCareEnvironmentCosts?: LocalCareEnvironmentCosts,
): number | undefined {
  if (!clientLocalCareEnvironmentCosts) {
    return undefined;
  }

  const careEnvironments: CareEnvironment[] = [
    CareEnvironment.home,
    CareEnvironment.assistedLiving,
    CareEnvironment.fullCareFacility,
    CareEnvironment.independentLiving,
  ];

  for (const careEnvironment of careEnvironments) {
    const environmentCost = clientLocalCareEnvironmentCosts[careEnvironment];
    if (environmentCost?.discount?.policyType) {
      return environmentCost.discount.policyType;
    }
  }

  return undefined;
}

const getHomeCareWaitingPeriodDays = (quote: Quote): number => {
  if (quote.carrierName === 'OneAmerica' || quote.carrierName === 'Lincoln Financial') {
    return 0;
  }
  return 90;
};

const getFacilityCareWaitingPeriodDays = (quote: Quote): number => {
  if (quote.carrierName === 'Lincoln Financial') {
    return 0;
  }
  return 90;
};

export const createPolicyFundingSourceFromQuote = (
  quote: Quote,
  client: Client,
  isAdvisorSession = false,
  advisorId?: string,
  updatedQuote?: Quote | null,
  illustrationPdfUrl?: string | null,
): PolicyFundingSource => {
  const calculateDailyBenefit = (quote: Quote): number => {
    if (
      quote.ltcBenefitMonthlyIndividual &&
      !isNaN(quote.ltcBenefitMonthlyIndividual) &&
      quote.ltcBenefitMonthlyIndividual > 0
    ) {
      return quote.ltcBenefitMonthlyIndividual / 30;
    }

    // Fallback: try using annual benefit if available
    if (
      quote.ltcBenefitAnnualIndividual &&
      !isNaN(quote.ltcBenefitAnnualIndividual) &&
      quote.ltcBenefitAnnualIndividual > 0
    ) {
      console.warn(
        `Using annual benefit for daily calculation for quote ${quote.insuranceQuoteId}`,
      );
      return quote.ltcBenefitAnnualIndividual / 365;
    }

    // Fallback: estimate from total LTC benefit
    // For most policies, assume 4-6 year benefit period as reasonable default
    if (quote.ltcBenefitTotal && !isNaN(quote.ltcBenefitTotal) && quote.ltcBenefitTotal > 0) {
      // Estimate 5-year benefit period: total benefit / (5 years * 365 days)
      const FIVE_YEARS_IN_DAYS = 1825;
      const estimatedDailyBenefit = quote.ltcBenefitTotal / FIVE_YEARS_IN_DAYS;
      console.warn(
        `ltcBenefitMonthlyIndividual missing for quote ${quote.insuranceQuoteId}. Estimated daily benefit from total: $${estimatedDailyBenefit.toFixed(2)}/day`,
      );
      return estimatedDailyBenefit;
    }

    // Final fallback: set to 0 to prevent NaN
    console.error(
      `Unable to calculate daily benefit for quote ${quote.insuranceQuoteId}. Setting to 0.`,
    );
    return 0;
  };

  const dailyBenefit = calculateDailyBenefit(quote);

  const isJointPolicy = quote.policyType === 'Joint Life';
  const policyType = getPolicyTypeFromProductName(quote.productName, isJointPolicy);
  const isSimpleInflation = quote.inflationBenefitOption?.toLowerCase().includes('simple') ?? false;

  const getIsIndemnityPolicyPayment = (quote: Quote): boolean => {
    if (quote.carrierName === 'OneAmerica') {
      return false;
    }
    if (policyType === FundingPolicyType.moneyGuardFixedAdvantage) {
      return false;
    }
    if (quote.carrierName === 'Lincoln Financial') {
      return true;
    }
    return true;
  };

  // Check if this is an unlimited policy (ltcBenefitTotal = 0 indicates unlimited)
  const isUnlimitedPolicy = quote.ltcBenefitTotal === 0;

  const inflationRate = getInflationRateFromQuote(quote);
  const inflationProtectionYears = getInflationProtectionYears(quote);
  const aobInflationParsed =
    quote.aobInflation && quote.aobInflation !== 'None' ? parseFloat(quote.aobInflation) : null;
  const cobInflationParsed =
    quote.cobInflation && quote.cobInflation !== 'None' ? parseFloat(quote.cobInflation) : null;

  // Check if this is a 10-Pay quote
  const is10PayQuote = quote.cobPaymentOption === '10-Pay';

  // For annuity products (like EquiTrust Bridge), use ltcBenefitTotal as the maximum benefit
  // since they don't have a death benefit
  const policyTypeDef = policyType ? fundingPolicyTypeDefs[policyType] : undefined;
  const isAnnuityProduct =
    policyTypeDef?.calculationType === FundingPolicyType.annuityHybrid ||
    policyType === FundingPolicyType.annuityHybrid;
  const maximumBenefitAmount = isAnnuityProduct ? quote.ltcBenefitTotal : quote.deathBenefitInitial;

  const basicPolicyFundingSource: PolicyFundingSource = {
    hasPolicyFunding: true,
    policyType,
    isSimpleInflationProtection: isSimpleInflation,
    isIndemnityPolicyPayment: getIsIndemnityPolicyPayment(quote),
    policyBenefitIsUnlimited: isUnlimitedPolicy,
    policyTotalLTCBenefit: isUnlimitedPolicy ? null : quote.ltcBenefitTotal,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    addedViaShopInsurance: true,
    insuranceQuoteId: quote.insuranceQuoteId,
    policyAddedAt: Date.now(),
    policyLumpSumPayment: is10PayQuote ? null : quote.premiumAmount,
    policyPremiumMonthlyCost: is10PayQuote ? quote.premiumAmount / 12 : 0,
    policyPremiumStartYear: new Date().getFullYear(),
    policyMaximumBenefitAmount: maximumBenefitAmount,
    dailyHomeBenefitAmount: dailyBenefit,
    dailyHomeInflationProtectionPercent: inflationRate / 100,
    dailyAssistedLivingBenefitAmount: dailyBenefit,
    dailyAssistedLivingInflationProtectionPercent: inflationRate / 100,
    dailyIndependentLivingBenefitAmount: dailyBenefit,
    dailyIndependentLivingInflationProtectionPercent: inflationRate / 100,
    dailyNursingHomeBenefitAmount: dailyBenefit,
    dailyNursingHomeInflationProtectionPercent: inflationRate / 100,
    homeCareWaitingPeriodDays: getHomeCareWaitingPeriodDays(quote),
    facilityCareWaitingPeriodDays: getFacilityCareWaitingPeriodDays(quote),
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(policyType)
      ? true
      : null,
    policyLimitedPayYears: is10PayQuote ? 10 : null,
    policyBenefitPeriodMonths: isCareMattersFamily(policyType)
      ? quote.ltcBenefitDurationMonths ?? null
      : null,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtection: calculatePolicyInflationProtection(
      inflationRate,
      quote.aobInflation,
      quote.cobInflation,
    ),
    policyInflationProtectionOnFaceAmount: aobInflationParsed
      ? aobInflationParsed / 100
      : cobInflationParsed !== null
        ? 0
        : null,
    policyInflationProtectionOnCOBAmount: cobInflationParsed ? cobInflationParsed / 100 : null,
    policyInflationProtectionYears: inflationProtectionYears,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: isUnlimitedPolicy
      ? null
      : quote.ltcBenefitTotal - (quote.deathBenefitInitial ?? 0),
    policyMinimumGuaranteedBenefitAmount:
      quote.guaranteedAccumulationValue ?? quote.deathBenefitMinimumGuaranteedIndividual ?? null,
    customPolicyId: null,
    sourceQuote: quote,
    updatedQuote: updatedQuote || null,
    illustrationPdfUrl: illustrationPdfUrl || null,
    // Include ROI values from updatedQuote if available
    policyProjectedReturnOnInvestment:
      updatedQuote?.roiValues?.projectedReturnOnInvestment ?? undefined,
    policyProjectedCompoundAnnualGrowthRate:
      updatedQuote?.roiValues?.projectedCompoundAnnualGrowthRate ?? undefined,
    policyProjectedInternalRateOfReturn:
      updatedQuote?.roiValues?.projectedInternalRateOfReturn ?? undefined,
    investmentPeriodYears: updatedQuote?.roiValues?.investmentPeriodYears ?? undefined,
  } as PolicyFundingSource;

  return basicPolicyFundingSource;
};

function calcMaxAchievableCoverage(
  updatedClient: Client,
  fundingSourceId: string,
  projectedCostCoverage: number,
): number {
  const phaseCalculations = updatedClient.policyPhaseCalculations?.[fundingSourceId];
  return (
    phaseCalculations?.reduce(
      (sum, phase) => sum + (phase.portionOfPhaseCostBenefitEligible ?? 0),
      0,
    ) ?? projectedCostCoverage
  );
}

/**
 * Calculates ROI for a policy based on calculation type
 */
export const calculateROI = (calculatedPolicy: PolicyFundingSource, calculationType = 'total') => {
  let roi = 0;

  switch (calculationType) {
    case 'compoundAnnualGrowthRate':
      roi = calculatedPolicy.policyProjectedCompoundAnnualGrowthRate || 0;
      break;
    case 'internalRateOfReturn':
      roi = calculatedPolicy.policyProjectedInternalRateOfReturn || 0;
      break;
    case 'total':
    default:
      roi = calculatedPolicy.policyProjectedReturnOnInvestment || 0;
  }

  return roi;
};

export const createUpdatedQuoteFromResponse = (
  sourceQuote: Quote,
  responseData: { [key: string]: string | null } | null,
): Quote => {
  if (!responseData) {
    return sourceQuote;
  }

  // Parse monetary values from formatted strings (e.g., "$6,855" -> 6855)
  const parseMonetaryValue = (value: string | null): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  };

  const updatedQuote: Quote = {
    ...sourceQuote,
    premiumAmount: responseData.premiumAmount
      ? parseMonetaryValue(responseData.premiumAmount)
      : sourceQuote.premiumAmount,

    ltcBenefitMonthlyIndividual: responseData.initialMonthlyBenBase
      ? parseMonetaryValue(responseData.initialMonthlyBenBase)
      : sourceQuote.ltcBenefitMonthlyIndividual,

    ltcBenefitAnnualIndividual: responseData.initialAnnualBenBase
      ? parseMonetaryValue(responseData.initialAnnualBenBase)
      : sourceQuote.ltcBenefitAnnualIndividual,

    deathBenefitInitial: responseData.deathBenefit
      ? parseMonetaryValue(responseData.deathBenefit)
      : sourceQuote.deathBenefitInitial,

    ltcBenefitTotal:
      responseData.totalInitialLTC === 'Unlimited'
        ? 0 // 0 indicates unlimited in our system
        : responseData.totalInitialLTC
          ? parseMonetaryValue(responseData.totalInitialLTC)
          : sourceQuote.ltcBenefitTotal,

    aobInflation: responseData.aobInflOption || sourceQuote.aobInflation,
    cobInflation: responseData.cobInflOption || sourceQuote.cobInflation,
    inflationDuration: responseData.inflDuration || sourceQuote.inflationDuration,
  };

  return updatedQuote;
};

export const createQuoteWithROIandProjectedCoverage = (
  quote: Quote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromQuote(quote, client);
  const tempFundingSourceId = `temp-quote-preview-${quote.insuranceQuoteId}`;

  const clientClone = cloneDeep(client);

  clientClone.multipleFundingSources.ltcPolicy = {
    ...clientClone.multipleFundingSources.ltcPolicy,
    [tempFundingSourceId]: basicPolicy,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  const projectedCostCoverage =
    policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0;

  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};

const getPremiumCostPerMonth = (quote: TraditionalLTCQuote): number => {
  switch (quote.premiumFrequency) {
    case 'Monthly':
      return quote.primaryModalPremium;
    case 'Annually':
      return quote.primaryModalPremium / 12;
    default:
      return quote.primaryModalPremium / 12;
  }
};

export const parseBioPercentToInflationRate = (bioPercent: string): number => {
  return bioPercent === 'No BIO' ? 0 : parseFloat(bioPercent.match(/(\d+)%/)?.[1] || '0') / 100;
};

export const createPolicyFundingSourceFromTraditionalLTCQuote = (
  quote: TraditionalLTCQuote,
  client: Client,
  isAdvisorSession = false,
  advisorId?: string,
): PolicyFundingSource => {
  const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);
  const policyType = getPolicyTypeFromProductName(quote.productName, false);

  const basicPolicy = {
    carrierName: quote.carrier,
    hasPolicyFunding: true,
    isSimpleInflationProtection: false,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    isIndemnityPolicyPayment: false,
    policyBenefitIsUnlimited: false,
    policyMaximumBenefitAmount: quote.benefitPool,
    policyPremiumMonthlyCost: getPremiumCostPerMonth(quote),
    policyPremiumStartYear: new Date().getFullYear(),
    policyInflationProtection: inflationRate,
    policyLumpSumPayment: null,
    dailyHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyHomeInflationProtectionPercent: null,
    dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyIndependentLivingInflationProtectionPercent: null,
    dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyAssistedLivingInflationProtectionPercent: null,
    dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyNursingHomeInflationProtectionPercent: null,
    homeCareWaitingPeriodDays: quote.eliminationPeriod,
    facilityCareWaitingPeriodDays: quote.eliminationPeriod,
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(policyType)
      ? true
      : null,
    policyLimitedPayYears: null,
    policyBenefitPeriodMonths: null,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtectionOnFaceAmount: null,
    policyInflationProtectionOnCOBAmount: null,
    policyInflationProtectionYears: null,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: null,
    policyMinimumGuaranteedBenefitAmount: null,
    customPolicyId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    addedViaShopInsurance: false,
    insuranceQuoteId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    sourceQuote: quote,
    policyType,
  };

  return basicPolicy as PolicyFundingSource;
};

export const createTraditionalLTCQuoteWithROIandProjectedCoverage = (
  quote: TraditionalLTCQuote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromTraditionalLTCQuote(quote, client);
  const tempFundingSourceId = `temp-trad-ltc-preview-${quote.traditionalLTCQuoteId}`;

  const clientClone = cloneDeep(client);

  clientClone.multipleFundingSources.ltcPolicy = {
    [tempFundingSourceId]: basicPolicy,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  const projectedCostCoverage =
    policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0;

  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  // Extract discount savings from allPhaseCosts
  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  // Extract discount policy type from any care environment that has a discount
  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};
